import React, { useEffect, useRef, useState } from 'react';

import {
  ArrowButtons,
  BreakpointConfigType,
  Breakpoints,
  Gallery,
  GallerySlide,
  Lentils,
} from '@volkswagen-onehub/components-core';
import { useIsMobile } from '@vw-marketing/us-components';

import { CarouselCard } from '../carousel-card/CarouselCard';
import {
  CustomGallerySlideWrapper,
  GalleryWrapper,
  StyledDesktopWrapper,
  StyledLentilsContainer,
  StyledSlideWrapper,
} from './styles';
import { ModelConfig } from '../../types/model';

interface VehiclesGalleryProps {
  vehicles: ModelConfig[];
}

const slidesPerPageConfig: BreakpointConfigType<number> = {
  [Breakpoints.default]: 1,
  [Breakpoints.b560]: 1,
  [Breakpoints.b960]: 2,
  [Breakpoints.b1280]: 3,
  [Breakpoints.b1600]: 4,
  [Breakpoints.b1920]: 4,
  [Breakpoints.b2560]: 4,
};

export const VehiclesGallery: React.FC<VehiclesGalleryProps> = ({
  vehicles,
}) => {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [currentWidth, setCurrentWidth] = useState<number>(window.innerWidth);
  const [isFirstSlide, setIsFirstSlide] = useState<boolean>(true);
  const [isLastSlide, setIsLastSlide] = useState<boolean>(false);

  const arrowButtonsContainerRef = useRef<HTMLDivElement>(null);
  
  const isMobile: boolean = useIsMobile(Breakpoints.b960);
  
  const isDesktop = currentWidth >= 1920;
  const vehiclesLength = vehicles.length;

  useEffect(() => {
    const handleResize = () => {
      setCurrentWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  /* useEffect to get the two arrow buttons at first render */
  useEffect(() => {
    const timer = setTimeout(() => {
      if (arrowButtonsContainerRef.current) {
        const prevButton = Array.from(
          arrowButtonsContainerRef.current.querySelectorAll('button'),
        ).find(
          (button) =>
            button.getAttribute('aria-label') === 'To previous vehicle',
        );

        const nextButton = Array.from(
          arrowButtonsContainerRef.current.querySelectorAll('button'),
        ).find(
          (button) => button.getAttribute('aria-label') === 'To next vehicle',
        );

        if (prevButton) {
          prevButton.style.visibility = isFirstSlide ? 'hidden' : 'visible';
          prevButton.style.opacity = isFirstSlide ? '0' : '1';
        }

        if (nextButton) {
          nextButton.style.visibility = isLastSlide ? 'hidden' : 'visible';
          nextButton.style.opacity = isLastSlide ? '0' : '1';
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [activeIndex]);

  const calculateCurrentSlides = () => {
    let currentSlidesPerPage = slidesPerPageConfig[Breakpoints.default];

    if (currentWidth >= 2560) {
      currentSlidesPerPage = slidesPerPageConfig[Breakpoints.b2560];
    } else if (currentWidth >= 1920) {
      currentSlidesPerPage = slidesPerPageConfig[Breakpoints.b1920];
    } else if (currentWidth >= 1600) {
      currentSlidesPerPage = slidesPerPageConfig[Breakpoints.b1600];
    } else if (currentWidth >= 1280) {
      currentSlidesPerPage = slidesPerPageConfig[Breakpoints.b1280];
    } else if (currentWidth >= 960) {
      currentSlidesPerPage = slidesPerPageConfig[Breakpoints.b960];
    } else if (currentWidth >= 560) {
      currentSlidesPerPage = slidesPerPageConfig[Breakpoints.b560];
    }

    return currentSlidesPerPage;
  };

  const handleChange = (newActiveIndex: number) => {
    setActiveIndex(newActiveIndex);
    setIsFirstSlide(newActiveIndex === 0);

    const currentSlidesPerPage = calculateCurrentSlides();

    // Last slide is when newActiveIndex + slidesPerPage >= total vehicles
    const isLast = newActiveIndex + currentSlidesPerPage >= vehiclesLength;
    setIsLastSlide(isLast);
  };

  return (
    <>
      {isDesktop ? (
        <StyledDesktopWrapper className="desktop-wrapper">
          {vehicles.map((vehicle, index) => {
            return (
              <StyledSlideWrapper
                key={`${vehicle.slug}-${index}`}
                className="slide-wrapper"
              >
                <CarouselCard
                  vehicle={vehicle}
                  key={`${vehicle.slug}-${index}`}
                />
              </StyledSlideWrapper>
            );
          })}
        </StyledDesktopWrapper>
      ) : (
        <GalleryWrapper className="gallery-wrapper">
          <Gallery
            id="models-carousel-gallery"
            aria-label="Models carousel gallery"
            bottomElements={
              vehiclesLength >= 2 ? (
                isMobile ? (
                  <StyledLentilsContainer>
                    <Lentils />
                  </StyledLentilsContainer>
                ) : (
                  <div ref={arrowButtonsContainerRef}>
                    <ArrowButtons
                      buttonPreviousAriaLabel="To previous vehicle"
                      buttonNextAriaLabel="To next vehicle"
                    />
                  </div>
                )
              ) : undefined
            }
            fadingEdges="auto"
            slidesPerPage={slidesPerPageConfig}
            gutter={isMobile ? 'grid001' : 'static350'}
            handleChange={(activeIndex: number) => handleChange(activeIndex)}
          >
            {vehicles.map((vehicle, index) => {
              return (
                <CustomGallerySlideWrapper>
                  <GallerySlide
                    aria-labelledby={`slide ${vehicle.slug} ${index}`}
                    key={`${vehicle.slug}-${index}`}
                  >
                    <CarouselCard vehicle={vehicle} />
                  </GallerySlide>
                </CustomGallerySlideWrapper>
              );
            })}
          </Gallery>
        </GalleryWrapper>
      )}
    </>
  );
};
